import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [bookingDetails, setBookingDetails] = useState(null);
  
  const bookingId = searchParams.get('booking_id');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyPayment = async () => {
      if (!bookingId || !sessionId) {
        setStatus('error');
        return;
      }

      try {
        // Verify payment status with Stripe (public endpoint - no auth required)
        const paymentStatus = await axios.get(`${API}/payments/status/public/${sessionId}`);
        
        if (paymentStatus.data.payment_status === 'paid') {
          // Get booking details
          const bookingResponse = await axios.get(`${API}/bookings/${bookingId}/public`);
          setBookingDetails(bookingResponse.data);
          setStatus('success');
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Payment verification error:', error);
        setStatus('error');
      }
    };

    verifyPayment();
  }, [bookingId, sessionId]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-amber-400 animate-spin mx-auto mb-4" />
          <p className="text-white text-xl">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">Payment Verification Failed</h1>
          <p className="text-zinc-400 mb-8">
            We couldn't verify your payment. Please contact us if you've been charged.
          </p>
          <Link to="/book">
            <Button className="bg-gradient-to-r from-amber-500 to-orange-600 text-white">
              Back to Booking
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Payment Successful! 🎉
            </h1>
            <p className="text-zinc-400 text-lg">
              Your booking has been confirmed and paid
            </p>
          </div>

          {bookingDetails && (
            <div className="space-y-4 mb-8">
              <div className="bg-zinc-800 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">Booking Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Booking ID:</span>
                    <span className="text-white font-mono">{bookingDetails.booking_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Vehicle:</span>
                    <span className="text-white">{bookingDetails.vehicle_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Pickup:</span>
                    <span className="text-white">{bookingDetails.pickup_location}</span>
                  </div>
                  {bookingDetails.destination && (
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Destination:</span>
                      <span className="text-white">{bookingDetails.destination}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Date & Time:</span>
                    <span className="text-white">
                      {new Date(bookingDetails.pickup_datetime).toLocaleString()}
                    </span>
                  </div>
                  
                  {/* Show different payment info based on payment_type */}
                  {bookingDetails.payment_type === 'full' ? (
                    <>
                      <div className="flex justify-between pt-3 border-t border-zinc-700">
                        <span className="text-green-400 font-semibold text-lg">✓ FULL PAYMENT:</span>
                        <span className="text-green-400 font-bold text-xl">
                          €{bookingDetails.fare.toFixed(2)}
                        </span>
                      </div>
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mt-2">
                        <p className="text-green-200 text-sm text-center font-semibold">
                          ✓ All paid - Nothing to pay to driver!
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between pt-3 border-t border-zinc-700">
                        <span className="text-zinc-400 font-semibold">Deposit Paid (10%):</span>
                        <span className="text-green-400 font-bold text-lg">
                          €{(bookingDetails.deposit_amount || bookingDetails.fare * 0.1).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between mt-2 pt-2 border-t border-zinc-700">
                        <span className="text-zinc-400">Total Service Cost:</span>
                        <span className="text-white font-semibold">€{bookingDetails.fare.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between mt-2">
                        <span className="text-zinc-400">Remaining Balance:</span>
                        <span className="text-amber-400 font-semibold">
                          €{(bookingDetails.fare - (bookingDetails.deposit_amount || bookingDetails.fare * 0.1)).toFixed(2)}
                        </span>
                      </div>
                      <p className="text-xs text-zinc-500 mt-2 text-center italic">
                        Pay remaining balance to driver after service
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
                <h3 className="text-amber-400 font-semibold mb-2">📧 Confirmation Email Sent</h3>
                <p className="text-zinc-300 text-sm">
                  We've sent a confirmation email to <strong>{bookingDetails.customer_email}</strong> with all your booking details.
                </p>
              </div>

              <div className="bg-zinc-800 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-2">What's Next?</h3>
                <ul className="text-zinc-300 text-sm space-y-2">
                  <li>✓ We've received your payment</li>
                  <li>✓ Our team will contact you before your pickup date</li>
                  <li>✓ Keep an eye on your email for updates</li>
                  <li>✓ Save this booking ID: <span className="font-mono text-amber-400">{bookingDetails.booking_id}</span></li>
                </ul>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/" className="flex-1">
              <Button variant="outline" className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Back to Home
              </Button>
            </Link>
            <Link to="/book" className="flex-1">
              <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white">
                Book Another Trip
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
